import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  type BillingReport,
  type BranchGroupSummary,
  type Client,
  type ProjectListEntry,
  type ProjectSummary,
  type TimeBlock,
} from '../../lib/api';
import { DateRangePicker, type DateRange } from '../../components/DateRangePicker';
import {
  FilterBar,
  EMPTY_FILTERS,
  filtersActive,
  type Filters,
} from '../../components/FilterBar';
import { StatsRow } from '../../components/StatsRow';
import { BillingCard } from '../../components/BillingCard';
import { BranchBreakdown } from '../../components/BranchBreakdown';
import { ProjectEditModal } from '../../components/ProjectEditModal';
import {
  blockBranch,
  deriveBilling,
  deriveBranches,
  deriveSummary,
} from '../../lib/derive';
import { formatDuration, formatTime, projectDisplayName, today } from '../../lib/format';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      project: ProjectListEntry;
      summary: ProjectSummary;
      blocks: TimeBlock[];
      billing: BillingReport;
      branches: BranchGroupSummary[];
    };

export function ProjectDetail() {
  const { projectKey: encoded } = useParams<{ projectKey: string }>();
  const projectKey = useMemo(() => (encoded ? decodeURIComponent(encoded) : ''), [encoded]);
  const [range, setRange] = useState<DateRange>({ from: today(), to: today() });
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [clients, setClients] = useState<Client[]>([]);
  const [allProjects, setAllProjects] = useState<ProjectListEntry[]>([]);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.listClients().then(setClients).catch(() => setClients([]));
    api.listProjects().then(setAllProjects).catch(() => setAllProjects([]));
  }, []);

  useEffect(() => {
    if (!projectKey) return;
    let cancelled = false;
    setState({ kind: 'loading' });

    Promise.all([
      api.getProject(projectKey),
      api.getSummary(projectKey, range.from, range.to),
      api.listBlocks(projectKey, range.from, range.to),
      api.getBilling(projectKey, range.from, range.to),
      api.getBranches(projectKey, range.from, range.to),
    ])
      .then(([project, summary, blocks, billing, branches]) => {
        if (!cancelled)
          setState({ kind: 'ready', project, summary, blocks, billing, branches });
      })
      .catch((err) => {
        if (!cancelled) setState({ kind: 'error', message: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [projectKey, range.from, range.to]);

  if (!projectKey) {
    return (
      <p className="font-display text-body text-black/70">Missing project key in URL.</p>
    );
  }

  const headerName =
    state.kind === 'ready'
      ? projectDisplayName(state.project)
      : projectKey.split('/').pop();

  return (
    <div>
      <Link
        to="/"
        className="mono-label text-black/50 hover:text-black transition-colors inline-flex items-center gap-2 mb-6"
      >
        <span>←</span> <span>back to projects</span>
      </Link>

      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mono-label text-black/50 mb-4">project</div>
          {state.kind === 'ready' && state.project.parent_project_key && (
            <Link
              to={`/projects/${encodeURIComponent(state.project.parent_project_key)}`}
              className="font-mono text-[12px] text-black/50 hover:text-midnight transition-colors inline-flex items-center gap-1 mb-2"
            >
              <span>↑</span>
              <span>
                {projectDisplayName(
                  allProjects.find(
                    (p) => p.project_key === state.project.parent_project_key
                  ) ?? {
                    project_key: state.project.parent_project_key,
                    name: state.project.parent_project_key.split('/').pop() ?? '',
                    display_name: null,
                  } as ProjectListEntry
                )}
              </span>
            </Link>
          )}
          <h1 className="font-display text-section font-medium tracking-section text-black break-words">
            {headerName}
          </h1>
          <p className="mt-3 font-mono text-[11px] text-black/40 break-all">{projectKey}</p>
          {state.kind === 'ready' && (state.project.client_name || state.project.tags.length > 0) && (
            <div className="mt-4 flex flex-wrap items-center gap-1.5">
              {state.project.client_name && (
                <span className="badge !bg-[#e8e4ff] !border-[#bdbbff] !text-midnight">
                  {state.project.client_name}
                </span>
              )}
              {state.project.tags.map((tag) => (
                <span key={tag} className="badge">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        {state.kind === 'ready' && (
          <button type="button" onClick={() => setEditing(true)} className="btn-secondary">
            Edit
          </button>
        )}
      </div>

      <div className="mb-8 space-y-3">
        <DateRangePicker
          value={range}
          onChange={setRange}
          minDate={state.kind === 'ready' ? state.project.created_at : undefined}
        />
        {state.kind === 'ready' && (
          <FilterBar
            value={filters}
            onChange={setFilters}
            branches={state.branches.map((b) => b.branch)}
            agents={state.summary.agent_breakdown.map((a) => a.agent_name)}
          />
        )}
      </div>

      {state.kind === 'loading' && (
        <div className="py-16 flex items-center justify-center">
          <span className="mono-label text-black/50">loading…</span>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="py-16">
          <p className="font-display text-body text-black/70">
            Could not load: {state.message}
          </p>
        </div>
      )}

      {state.kind === 'ready' && (() => {
        const active = filtersActive(filters);
        const filteredBlocks = active
          ? state.blocks.filter(
              (b) =>
                (filters.branch === 'all' || blockBranch(b) === filters.branch) &&
                (filters.type === 'all' || b.type === filters.type) &&
                (filters.agent === 'all' || b.agent_name === filters.agent)
            )
          : state.blocks;
        const summary = active ? deriveSummary(state.summary, filteredBlocks) : state.summary;
        const billing = active ? deriveBilling(state.billing, summary) : state.billing;
        const branches = active ? deriveBranches(filteredBlocks) : state.branches;

        return (
        <>
          <StatsRow summary={summary} />
          <BillingCard report={billing} />
          <SubProjectsCard
            projectId={state.project.id}
            allProjects={allProjects}
          />
          <BranchBreakdown branches={branches} />
          <AgentBreakdown summary={summary} />
          <BlocksTable blocks={filteredBlocks} />

          <ProjectEditModal
            open={editing}
            project={state.project}
            clients={clients}
            allProjects={allProjects}
            onClose={() => setEditing(false)}
            onSaved={(updated) => {
              setState((prev) =>
                prev.kind === 'ready' ? { ...prev, project: updated } : prev
              );
              setAllProjects((prev) =>
                prev.map((p) => (p.id === updated.id ? updated : p))
              );
            }}
          />
        </>
        );
      })()}
    </div>
  );
}

function SubProjectsCard({
  projectId,
  allProjects,
}: {
  projectId: string;
  allProjects: ProjectListEntry[];
}) {
  const children = allProjects.filter((p) => p.parent_project_id === projectId);
  if (children.length === 0) return null;
  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-sub font-medium tracking-sub text-black">
          Sub-projects
        </h2>
        <span className="mono-label text-black/50">
          {children.length} {children.length === 1 ? 'child' : 'children'}
        </span>
      </div>
      <div className="card overflow-hidden">
        <ul className="divide-y divide-border-light">
          {children.map((c) => (
            <li key={c.project_key}>
              <Link
                to={`/projects/${encodeURIComponent(c.project_key)}`}
                className="flex items-center justify-between px-5 py-4 hover:bg-glass-dark transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-display text-body font-medium text-black truncate">
                    {projectDisplayName(c)}
                  </div>
                  <div className="font-mono text-[11px] text-black/40 truncate mt-0.5">
                    {c.project_key}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  {c.client_name && (
                    <span className="badge !bg-[#e8e4ff] !border-[#bdbbff] !text-midnight">
                      {c.client_name}
                    </span>
                  )}
                  <span className="mono-label text-black/30">→</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function AgentBreakdown({ summary }: { summary: ProjectSummary }) {
  if (summary.agent_breakdown.length === 0) {
    return null;
  }
  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-sub font-medium tracking-sub text-black">
          Per-agent breakdown
        </h2>
        <span className="mono-label text-black/50">
          {summary.agent_breakdown.length} {summary.agent_breakdown.length === 1 ? 'agent' : 'agents'}
        </span>
      </div>
      <div className="card overflow-hidden">
        <ul className="divide-y divide-border-light">
          {summary.agent_breakdown.map((row) => (
            <li
              key={row.agent_name}
              className="flex items-center justify-between px-5 py-4 hover:bg-glass-dark transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="badge">{row.agent_name}</span>
                <span className="mono-label text-black/50">
                  {row.block_count} {row.block_count === 1 ? 'block' : 'blocks'}
                </span>
              </div>
              <span className="font-display text-feature font-medium tracking-feature text-[#b45309] tabular-nums">
                {formatDuration(row.labor_seconds)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function BlocksTable({ blocks }: { blocks: TimeBlock[] }) {
  if (blocks.length === 0) {
    return (
      <section className="mt-10">
        <h2 className="font-display text-sub font-medium tracking-sub text-black mb-4">
          Blocks
        </h2>
        <div className="card p-8 text-center">
          <span className="mono-label text-black/50">no blocks in this range</span>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-sub font-medium tracking-sub text-black">Blocks</h2>
        <span className="mono-label text-black/50">{blocks.length} total</span>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-glass-dark">
            <tr>
              <Th>Type</Th>
              <Th>Agent</Th>
              <Th>Started</Th>
              <Th>Ended</Th>
              <Th className="text-right">Duration</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-light">
            {blocks.map((block) => (
              <tr key={block.block_id} className="hover:bg-glass-dark/50 transition-colors">
                <td className="px-5 py-3">
                  <span
                    className={`badge ${
                      block.type === 'human'
                        ? '!bg-[#dcfce7] !border-[#86efac]'
                        : '!bg-[#fef3c7] !border-[#fcd34d]'
                    }`}
                  >
                    {block.type}
                  </span>
                </td>
                <td className="px-5 py-3 font-mono text-[11px] text-black/70">
                  {block.agent_name ?? '—'}
                </td>
                <td className="px-5 py-3 font-mono text-[11px] text-black/70 tabular-nums">
                  {formatTime(block.started_at)}
                </td>
                <td className="px-5 py-3 font-mono text-[11px] text-black/70 tabular-nums">
                  {block.ended_at ? formatTime(block.ended_at) : '(open)'}
                </td>
                <td className="px-5 py-3 font-display text-body font-medium text-right tabular-nums">
                  {block.duration_seconds !== null ? formatDuration(block.duration_seconds) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`mono-label text-black/50 text-left px-5 py-3 ${className ?? ''}`}>
      {children}
    </th>
  );
}
